// Page Choix des thèmes à améliorer - V4 shadcn
// Checkbox, ToggleGroup, Badge, Alert, Card, Tabs multi-métiers

import { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Checkbox } from '../components/ui/checkbox'
import { Badge } from '../components/ui/badge'
import { Alert, AlertDescription } from '../components/ui/alert'
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group'
import { CheckSquare } from 'lucide-react'
import { cn } from '../lib/utils'
import { normalizeThemeName } from '../lib/theme-utils'
import { api } from '../services/api'
import { toast } from 'sonner'

// Interface pour les données de priorité OPPBTP (priorites-oppbtp.json)
interface PrioriteOppbtpData {
  metierId: string
  questionnaire: string
  sujet: string
  niveauPriorite: number // 1=Haute, 2=Moyenne, 3=Faible
  classementEntreprise: number
}

const PRIORITE_LABELS: Record<number, string> = {
  1: 'Indispensable',
  2: 'Importante',
  // 3: pas de badge affiché
}

// Alias pour les thèmes dont les noms divergent entre questionnaire et référentiel
const THEME_ALIASES: Record<string, string> = {
  'gestion secours': 'urgences et secours',
  'gestion des secours': 'urgences et secours',
}

interface ThemeScore {
  id: string
  nom: string
  total: number
  max: number
  moyenne: number // pourcentage 0-100
  questionnaire: 'Q1' | 'Q2'
  metierId?: string // pour Q1
  prioriteOppbtp?: string // V6: "Haute", "Moyenne", "Faible"
}

interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Couleur sémantique des barres de score
function getScoreBarColor(percent: number): string {
  if (percent >= 80) return 'bg-primary'
  if (percent >= 60) return 'bg-primary/60'
  if (percent >= 40) return 'bg-amber-400'
  return 'bg-red-400'
}

function getScoreTextColor(percent: number): string {
  if (percent >= 80) return 'text-primary'
  if (percent >= 60) return 'text-primary/80'
  if (percent >= 40) return 'text-amber-600'
  return 'text-red-600'
}

type SortMode = 'score' | 'priorite'

export default function ChoixThemesPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [questionsQ1, setQuestionsQ1] = useState<any[]>([])
  const [questionsQ2, setQuestionsQ2] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedThemes, setSelectedThemes] = useState<Set<string>>(new Set())
  const [sortMode, setSortMode] = useState<SortMode>('score')
  const [prioritesData, setPrioritesData] = useState<PrioriteOppbtpData[]>([])

  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const steps = useDiagnosticSteps('themes', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Diagnostic chargé (pour réutiliser dans restoreSelections sans double appel API)
  const loadedDiagnosticRef = useRef<any>(null)

  // Charger les données
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)

      try {
        const diagnostic = await api.getDiagnostic(diagnosticId)
        loadedDiagnosticRef.current = diagnostic

        const reponsesData: ReponseData[] = (diagnostic?.reponses || []).map((r: any) => ({
          questionId: r.questionId,
          metierId: r.metier?.code || r.metierId,
          valeur: r.valeur,
        }))
        setReponses(reponsesData)

        const [q1, q2] = await Promise.all([
          api.getQuestionsQ1(),
          api.getQuestionsQ2(),
        ])
        setQuestionsQ1(q1)
        setQuestionsQ2(q2)

        try {
          const response = await fetch('/data/referentiel/priorites-oppbtp.json')
          if (response.ok) {
            const data = await response.json()
            setPrioritesData(data.data || [])
          }
        } catch (err) {
          console.warn('Priorités OPPBTP non disponibles:', err)
        }
      } catch (err) {
        console.error('Erreur chargement données:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Mapping des priorités OPPBTP par nom de thème normalisé (Risques + Management)
  const prioritesMap = useMemo(() => {
    const map = new Map<string, number>() // normalized sujet → meilleur niveauPriorite
    const selectedMetierSet = new Set(metierIds)

    prioritesData
      .filter(p => selectedMetierSet.has(p.metierId))
      .forEach(p => {
        const key = normalizeThemeName(p.sujet)
        const existing = map.get(key)
        if (!existing || p.niveauPriorite < existing) {
          map.set(key, p.niveauPriorite)
        }
      })

    return map
  }, [prioritesData, metierIds])

  // Recherche la priorité OPPBTP pour un nom de thème (fuzzy match)
  const findPriorite = (themeName: string): string | undefined => {
    let normalized = normalizeThemeName(themeName)
    // Alias
    if (THEME_ALIASES[normalized]) {
      normalized = THEME_ALIASES[normalized]
    }

    // Match exact
    const exact = prioritesMap.get(normalized)
    if (exact) return PRIORITE_LABELS[exact]

    // Match préfixe (dans les 2 sens)
    for (const [key, value] of prioritesMap) {
      if (key.startsWith(normalized) || normalized.startsWith(key)) {
        return PRIORITE_LABELS[value]
      }
    }

    return undefined
  }

  // Scores par thème Q1 (agrégé par thème, tous métiers)
  const themesQ1 = useMemo<ThemeScore[]>(() => {
    const themeScoresMap = new Map<string, { total: number; count: number; questionIds: Set<string> }>()

    metierIds.forEach((metierId) => {
      const metierReponses = reponses.filter(r => r.metierId === metierId)

      metierReponses.forEach((reponse) => {
        const question = questionsQ1.find(q => q.id === reponse.questionId)
        if (question) {
          const themeName = question.themeQuestion
            || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
            || 'Thème inconnu'

          if (!themeScoresMap.has(themeName)) {
            themeScoresMap.set(themeName, { total: 0, count: 0, questionIds: new Set() })
          }

          const current = themeScoresMap.get(themeName)!
          current.total += reponse.valeur
          current.count += 1
          current.questionIds.add(reponse.questionId)
        }
      })
    })

    const themes: ThemeScore[] = []
    themeScoresMap.forEach((scores, themeName) => {
      const max = scores.count * 3
      const moyenne = max > 0 ? (scores.total / max) * 100 : 0

      themes.push({
        id: `q1-${themeName}`,
        nom: themeName,
        total: scores.total,
        max,
        moyenne,
        questionnaire: 'Q1',
        prioriteOppbtp: findPriorite(themeName),
      })
    })

    return themes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metierIds, reponses, questionsQ1, prioritesMap])

  // Scores par thème Q2
  const themesQ2 = useMemo<ThemeScore[]>(() => {
    const themeScoresMap = new Map<string, { total: number; count: number; questionIds: Set<string> }>()

    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question) {
        const themeName = question.themeQuestion
          || (typeof question.theme === 'object' && question.theme !== null ? question.theme.nom : null)
          || 'Thème inconnu'

        if (!themeScoresMap.has(themeName)) {
          themeScoresMap.set(themeName, { total: 0, count: 0, questionIds: new Set() })
        }

        const current = themeScoresMap.get(themeName)!
        current.total += reponse.valeur
        current.count += 1
        current.questionIds.add(reponse.questionId)
      }
    })

    const themes: ThemeScore[] = []
    themeScoresMap.forEach((scores, themeName) => {
      const max = scores.count * 3
      const moyenne = max > 0 ? (scores.total / max) * 100 : 0

      themes.push({
        id: `q2-${themeName}`,
        nom: themeName,
        total: scores.total,
        max,
        moyenne,
        questionnaire: 'Q2',
        prioriteOppbtp: findPriorite(themeName),
      })
    })

    return themes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reponses, questionsQ2, prioritesMap])

  // Restaurer les thèmes sauvegardés en BDD (si on revient sur la page)
  // Utilise le diagnostic déjà chargé par loadData (pas de double appel API)
  useEffect(() => {
    if (!diagnosticId || isLoading) return
    // Si on a déjà des sélections, ne pas écraser
    if (selectedThemes.size > 0) return

    const diagnostic = loadedDiagnosticRef.current
    if (!diagnostic) return

    const savedThemes = diagnostic?.selectedThemes as string[] | null
    if (savedThemes && savedThemes.length > 0) {
      const allThemes = [...themesQ1, ...themesQ2]
      const restoredIds = new Set<string>()
      savedThemes.forEach((name: string) => {
        const found = allThemes.find(t => t.nom === name)
        if (found) restoredIds.add(found.id)
      })
      if (restoredIds.size > 0) {
        setSelectedThemes(restoredIds)
      }
    }
  }, [diagnosticId, isLoading, themesQ1, themesQ2]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sauvegarde des thèmes en BDD (debounced 1s)
  const isInitialThemeLoad = useRef(true)
  const themeSaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestSelectedThemes = useRef(selectedThemes)
  latestSelectedThemes.current = selectedThemes

  const saveThemesToDb = useCallback(async (themeIds: Set<string>) => {
    if (!diagnosticId || themeIds.size === 0) return
    const themeNames = [...themesQ1, ...themesQ2]
      .filter(t => themeIds.has(t.id))
      .map(t => t.nom)
    if (themeNames.length === 0) return
    try {
      await api.saveSelections(diagnosticId, { selectedThemes: themeNames })
    } catch (err) {
      console.error('Auto-save thèmes erreur:', err)
    }
  }, [diagnosticId, themesQ1, themesQ2])

  useEffect(() => {
    // Ne pas sauvegarder au chargement initial (restauration depuis BDD)
    if (isInitialThemeLoad.current) {
      isInitialThemeLoad.current = false
      return
    }
    if (isLoading) return

    if (themeSaveTimeout.current) clearTimeout(themeSaveTimeout.current)
    themeSaveTimeout.current = setTimeout(() => {
      saveThemesToDb(selectedThemes)
    }, 1000)

    return () => {
      if (themeSaveTimeout.current) clearTimeout(themeSaveTimeout.current)
    }
  }, [selectedThemes, isLoading, saveThemesToDb])

  // Flush la sauvegarde quand l'utilisateur quitte la page
  useEffect(() => {
    return () => {
      if (themeSaveTimeout.current) {
        clearTimeout(themeSaveTimeout.current)
        saveThemesToDb(latestSelectedThemes.current)
      }
    }
  }, [saveThemesToDb])

  // Les 3 pires thèmes toutes colonnes confondues (recommandés)
  const recommendedThemes = useMemo(() => {
    const all = [...themesQ1, ...themesQ2]
    const sorted = all.sort((a, b) => a.moyenne - b.moyenne)
    return new Set(sorted.slice(0, 3).map(t => t.id))
  }, [themesQ1, themesQ2])

  // Fonction de tri partagée
  const sortThemes = (themes: ThemeScore[], mode: SortMode) => {
    const ordreMap: Record<string, number> = { 'Indispensable': 1, 'Importante': 2 }
    if (mode === 'score') {
      return [...themes].sort((a, b) => a.moyenne - b.moyenne)
    }
    return [...themes].sort((a, b) => {
      const prioA = a.prioriteOppbtp ? (ordreMap[a.prioriteOppbtp] || 99) : 99
      const prioB = b.prioriteOppbtp ? (ordreMap[b.prioriteOppbtp] || 99) : 99
      if (prioA !== prioB) return prioA - prioB
      return a.moyenne - b.moyenne
    })
  }

  // Thèmes Q1 triés
  const sortedThemesQ1 = useMemo(() => sortThemes(themesQ1, sortMode), [themesQ1, sortMode])

  // Thèmes Q2 triés
  const sortedThemesQ2 = useMemo(() => sortThemes(themesQ2, sortMode), [themesQ2, sortMode])

  // Toggle sélection thème
  const toggleTheme = (themeId: string) => {
    setSelectedThemes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(themeId)) {
        newSet.delete(themeId)
      } else {
        newSet.add(themeId)
      }
      return newSet
    })
  }

  // Validation (au moins 1 thème)
  const canContinue = selectedThemes.size >= 1

  // Navigation
  const handleContinue = async () => {
    trackEvent('valider themes')
    const selectedThemeNames = [...themesQ1, ...themesQ2]
      .filter(t => selectedThemes.has(t.id))
      .map(t => t.nom)

    // Sauvegarder les thèmes sélectionnés en BDD
    if (diagnosticId) {
      try {
        await api.saveSelections(diagnosticId, { selectedThemes: selectedThemeNames })
      } catch (err) {
        console.error('Erreur sauvegarde sélections:', err)
        toast.error('Erreur lors de la sauvegarde des thèmes')
      }
    }

    navigate(`/diagnostic/${diagnosticId}/solutions`, {
      state: {
        ...diagnosticState,
        selectedThemes: selectedThemeNames,
      },
    })
  }

  const handleBack = () => {
    navigate(`/diagnostic/${diagnosticId}/casques`, {
      state: diagnosticState,
    })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des thèmes...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={handleContinue}
      backLabel="Casques"
      nextLabel={canContinue ? 'Catalogue des solutions' : 'Sélectionnez au moins un thème'}
      nextDisabled={!canContinue}
      diagnosticState={diagnosticState}
    >
      {/* Header de page */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Choisir les thèmes à améliorer</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Conseil OPPBTP : idéalement, sélectionnez jusqu'à 3 thèmes, mais vous pouvez en choisir davantage si nécessaire.
        </p>
      </div>

      {/* Compteur sélection */}
      <Alert
        className={cn(
          'mb-6',
          canContinue
            ? 'border-primary/30 bg-primary/5'
            : 'border-border'
        )}
      >
        <CheckSquare className={cn(
          'size-4',
          canContinue ? 'text-primary' : 'text-muted-foreground'
        )} />
        <AlertDescription className="text-sm">
          {selectedThemes.size === 0 ? (
            <span className="text-muted-foreground">
              Cochez les thèmes sur lesquels vous souhaitez agir
            </span>
          ) : (
            <>
              <span className={cn('font-semibold', canContinue ? 'text-primary' : 'text-foreground')}>
                {selectedThemes.size} thème{selectedThemes.size > 1 ? 's' : ''} sélectionné{selectedThemes.size > 1 ? 's' : ''}
              </span>
              {canContinue && (
                <span className="text-muted-foreground ml-1">
                  Votre plan d'actions est prêt
                </span>
              )}
            </>
          )}
        </AlertDescription>
      </Alert>

      {/* Grille côte à côte : Risques | Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Colonne Risques (Q1) */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </span>
                Risques
              </h2>
              <ToggleGroup
                type="single"
                variant="outline"
                value={sortMode}
                onValueChange={(v) => v && setSortMode(v as SortMode)}
              >
                <ToggleGroupItem value="score" className="text-xs px-2 h-7">
                  Score
                </ToggleGroupItem>
                <ToggleGroupItem value="priorite" className="text-xs px-2 h-7">
                  Priorité
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {sortedThemesQ1.length === 0 ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Aucun thème trouvé</p>
            ) : (
              <ThemesList
                themes={sortedThemesQ1}
                selected={selectedThemes}
                recommended={recommendedThemes}
                onToggle={toggleTheme}
              />
            )}
          </CardContent>
        </Card>

        {/* Colonne Management (Q2) */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </span>
                Management
              </h2>
              <ToggleGroup
                type="single"
                variant="outline"
                value={sortMode}
                onValueChange={(v) => v && setSortMode(v as SortMode)}
              >
                <ToggleGroupItem value="score" className="text-xs px-2 h-7">
                  Score
                </ToggleGroupItem>
                <ToggleGroupItem value="priorite" className="text-xs px-2 h-7">
                  Priorité
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {sortedThemesQ2.length === 0 ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Aucun thème trouvé</p>
            ) : (
              <ThemesList
                themes={sortedThemesQ2}
                selected={selectedThemes}
                recommended={recommendedThemes}
                onToggle={toggleTheme}
              />
            )}
          </CardContent>
        </Card>
      </div>

    </DiagnosticLayout>
  )
}

// Composant liste de thèmes réutilisé pour Q1 et Q2
function ThemesList({
  themes,
  selected,
  recommended,
  onToggle,
}: {
  themes: ThemeScore[]
  selected: Set<string>
  recommended: Set<string>
  onToggle: (id: string) => void
}) {
  return (
    <div className="space-y-1">
      {themes.map((theme, index) => {
        const isSelected = selected.has(theme.id)
        const isRecommended = recommended.has(theme.id)
        const rank = index + 1
        const scorePercent = Math.round(theme.moyenne)

        return (
          <div
            key={theme.id}
            onClick={() => onToggle(theme.id)}
            className={cn(
              'flex items-start gap-2.5 py-2.5 px-3 rounded-lg border cursor-pointer transition-colors',
              isSelected
                ? 'border-primary bg-primary/5'
                : isRecommended
                  ? 'border-primary/30 bg-primary/5 hover:bg-primary/10'
                  : 'border-border hover:bg-muted/50'
            )}
          >
            {/* Checkbox */}
            <Checkbox
              checked={isSelected}
              className="flex-shrink-0 mt-0.5 pointer-events-none"
            />

            {/* Rang */}
            <span className={cn(
              'w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5',
              rank <= 3
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            )}>
              {rank}
            </span>

            {/* Contenu principal */}
            <div className="flex-1 min-w-0 space-y-1.5">
              {/* Ligne 1 : nom + priorité OPPBTP + % */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={cn(
                    'text-sm leading-tight truncate',
                    isSelected ? 'font-medium text-foreground' : 'text-foreground'
                  )}>
                    {theme.nom}
                  </span>
                  {theme.prioriteOppbtp && (
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] leading-none py-0.5 px-1.5 flex-shrink-0',
                        theme.prioriteOppbtp === 'Indispensable'
                          ? 'border-red-300 text-red-700 bg-red-50'
                          : 'border-amber-300 text-amber-700 bg-amber-50'
                      )}
                    >
                      {theme.prioriteOppbtp}
                    </Badge>
                  )}
                </div>
                <span className={cn(
                  'text-sm font-semibold tabular-nums flex-shrink-0',
                  getScoreTextColor(scorePercent)
                )}>
                  {theme.total}/{theme.max}
                </span>
              </div>

              {/* Ligne 2 : barre pleine largeur + badge recommandé */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      getScoreBarColor(scorePercent)
                    )}
                    style={{ width: `${scorePercent}%` }}
                  />
                </div>
                {isRecommended && !isSelected && (
                  <Badge
                    className="text-[10px] leading-none py-0.5 px-1.5 bg-primary text-primary-foreground flex-shrink-0"
                  >
                    Recommandé
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
